import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { services } from "../data/mindscope";
import { Reveal } from "../components/mindscope/Reveal";

const tags = ["All", "Equity", "Debt", "Hybrid", "M&A", "Public Markets", "Funds", "Capital Return", "Capital Structure", "Strategic Exit", "Growth", "Advisory"];

// Bento column-span for asymmetric layout on lg — keyed by service num
const bentoBySpan = {
  "01": "lg:col-span-6", "02": "lg:col-span-3", "03": "lg:col-span-3",
  "04": "lg:col-span-3", "05": "lg:col-span-3", "06": "lg:col-span-6",
  "07": "lg:col-span-4", "08": "lg:col-span-4", "09": "lg:col-span-4",
  "10": "lg:col-span-3", "11": "lg:col-span-6", "12": "lg:col-span-3",
  "13": "lg:col-span-4", "14": "lg:col-span-4", "15": "lg:col-span-4",
};

export default function Services() {
  const [active, setActive] = useState("All");
  const [selected, setSelected] = useState(null);

  const filtered = useMemo(
    () => (active === "All" ? services : services.filter((s) => s.tag === active)),
    [active]
  );

  return (
    <main data-testid="services-page" className="bg-ms-navy text-white overflow-hidden">
      {/* Header */}
      <section className="pt-40 pb-16 bg-ms-navy grain relative">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="eyebrow mb-6">Chapter Five</div>
            <h1 className="font-display text-6xl md:text-8xl lg:text-[10rem] leading-[0.9] tracking-[-0.03em]">
              Strategic <span className="italic text-ms-gold">Capital Solutions</span>
            </h1>
            <p className="mt-8 max-w-2xl font-body text-white/70 text-lg leading-relaxed">
              A full-spectrum practice across equity, debt, funds and advisory — engineered as a single,
              interoperable ecosystem. Filter to explore.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Filter bar */}
      <section className="sticky top-20 z-30 bg-ms-navy/85 backdrop-blur-xl border-y border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-4 flex gap-2 flex-wrap overflow-x-auto">
          {tags.map((t) => {
            const count = t === "All" ? services.length : services.filter((s) => s.tag === t).length;
            return (
              <button
                key={t}
                onClick={() => setActive(t)}
                data-testid={`filter-${t.toLowerCase().replace(/\s+/g, "-")}`}
                className={`font-body text-[0.72rem] tracking-[0.2em] uppercase px-4 py-2 rounded-full border transition-colors duration-300 flex items-center gap-2 ${
                  active === t
                    ? "bg-ms-gold border-ms-gold text-ms-navy"
                    : "bg-transparent border-white/15 text-white/70 hover:border-ms-gold hover:text-ms-gold"
                }`}
              >
                {t}
                <span className={`text-[0.55rem] tracking-normal ${active === t ? "text-ms-navy/70" : "text-white/40"}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {/* Bento grid — plain grid, no stagger container to avoid filter re-animation glitches */}
      <section className="py-16 md:py-24 bg-ms-navy">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 min-h-[400px]">
          <div
            key={active}
            data-testid={`services-grid-${active.toLowerCase().replace(/\s+/g, "-")}`}
            data-active-filter={active}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-3 md:gap-4"
          >
            {filtered.map((s, i) => (
              <motion.div
                key={s.num}
                layout="position"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: Math.min(i * 0.04, 0.4), ease: [0.16, 1, 0.3, 1] }}
                className={bentoBySpan[s.num] || "lg:col-span-4"}
              >
                <button
                  onClick={() => setSelected(s)}
                  data-testid={`service-card-${s.num}`}
                  className="text-left w-full h-full bg-[#14264A] hover:bg-[#182D57] p-6 md:p-8 gold-border-hover group relative overflow-hidden min-h-[220px] flex flex-col justify-between"
                >
                  <div className="flex items-start justify-between">
                    <span className="font-display text-lg text-ms-gold">{s.num}</span>
                    <span className="text-[0.6rem] tracking-[0.28em] uppercase text-white/40 border border-white/10 rounded-full px-2.5 py-1">
                      {s.tag}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-display text-3xl md:text-4xl text-white leading-[0.95] mb-3 group-hover:text-ms-gold transition-colors duration-500">
                      {s.title}
                    </h3>
                    <p className="font-body text-sm md:text-base text-white/70 leading-relaxed line-clamp-3">
                      {s.desc}
                    </p>
                  </div>
                  <div className="mt-6 flex items-center gap-2 text-ms-gold text-[0.65rem] tracking-[0.28em] uppercase">
                    Read Brief <ArrowUpRight size={14} className="group-hover:-translate-y-1 group-hover:translate-x-1 transition-transform duration-500" />
                  </div>
                  <div className="absolute -right-16 -bottom-16 w-40 h-40 rounded-full bg-ms-gold/5 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                </button>
              </motion.div>
            ))}
          </div>
          {filtered.length === 0 && (
            <div className="text-center py-24 text-white/50 font-display italic text-2xl">
              No instruments in this category yet.
            </div>
          )}
        </div>
      </section>

      {/* Modal */}
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-md flex items-center justify-center p-4"
            onClick={() => setSelected(null)}
            data-testid="service-modal-backdrop"
          >
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 20, opacity: 0 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-2xl w-full bg-[#0A1128] border border-ms-gold/30 rounded-sm p-8 md:p-12 relative"
              onClick={(e) => e.stopPropagation()}
              data-testid="service-modal"
            >
              <button
                onClick={() => setSelected(null)}
                data-testid="service-modal-close"
                className="absolute top-4 right-4 text-white/60 hover:text-ms-gold w-9 h-9 rounded-full border border-white/20 flex items-center justify-center transition-colors duration-300"
                aria-label="Close"
              >
                ×
              </button>
              <div className="flex items-baseline justify-between mb-6">
                <span className="font-display text-5xl text-ms-gold">{selected.num}</span>
                <span className="text-[0.65rem] tracking-[0.3em] uppercase text-white/50">{selected.tag}</span>
              </div>
              <h3 className="font-display text-4xl md:text-5xl text-white leading-[0.95] mb-6">
                {selected.title}
              </h3>
              <p className="font-body text-white/80 text-lg leading-relaxed">{selected.desc}</p>
              <div className="hairline my-8 opacity-30" />
              <p className="font-display italic text-white/60 mb-6">
                Every {selected.title.toLowerCase()} mandate begins with a private diagnostic — call
                zero, no obligation.
              </p>
              <Link to="/contact" className="btn-gold" onClick={() => setSelected(null)}>
                Begin a Dialogue <ArrowRight size={16} />
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CTA */}
      <section className="section-y bg-[#050914]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-end">
          <Reveal className="lg:col-span-8">
            <div className="eyebrow mb-4">Continue</div>
            <h3 className="font-display text-4xl md:text-6xl leading-[0.95] text-white">
              Not sure which instrument fits? <span className="italic text-ms-gold">Neither were they, at first.</span>
            </h3>
          </Reveal>
          <Link to="/contact" data-testid="services-next-chapter" className="btn-gold lg:col-span-3 lg:col-start-10 justify-center">
            Begin Dialogue <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </main>
  );
}
